import { AUTO_MATCH_FLOOR, estimatedCookedWeight, type RecipeCooking } from '@gramas/shared'
import { Button, Card, Chip, EmptyState, List, ListRow, NumberField, Pill, Sheet } from '@gramas/ui'
import { useEffect, useState } from 'react'
import { api } from '../../api/client.js'
import type { LocalFood } from '../../db/dexie.js'
import { cacheFood, foodDisplayName, saveRecipe } from '../../db/repo.js'
import { PersonaSpinner } from '../../glitime/PersonaSpinner.js'
import { sassLine } from '../../sass/engine.js'
import { useSassOnce } from '../../sass/useSassOnce.js'
import { syncNow } from '../../sync/engine.js'
import { CookedWeightField } from './CookedWeightField.js'
import { FoodPickerSheet } from './FoodPickerSheet.js'

interface WireCandidate {
  food: LocalFood
  similarity: number
  /** How well the catalogue name answers the line — see `nameMatchStrength`. */
  strength?: number
}

interface WireIngredient {
  raw: string
  name: string
  grams: number | null
  optional: boolean
  candidates: WireCandidate[]
}

interface WireRecipe {
  title: string | null
  servings: number | null
  source: 'json-ld' | 'text'
  url: string
  ingredients: WireIngredient[]
  /** The page listed more lines than the reader will carry — say so, never hide it. */
  truncated?: boolean
}

interface Line {
  key: string
  raw: string
  heard: string
  grams: number
  food: LocalFood | null
  candidates: LocalFood[]
  /** Off by default for an ingredient the recipe itself called optional. */
  included: boolean
}

type Phase =
  | { name: 'input' }
  | { name: 'loading' }
  | {
      name: 'review'
      title: string
      servings: number | null
      lines: Line[]
      source: string
      truncated: boolean
    }
  | { name: 'failed'; message: string }

/**
 * A recipe from a link.
 *
 * The page is read on the server — nearly every recipe site publishes its
 * ingredient list as structured data, so the lines are the author's own words
 * rather than a reconstruction — and the model only turns those lines into
 * grams. What arrives here is a list to confirm, never a recipe already made:
 * "500 g pomidorų" matches nine tomatoes in the catalogue and only the cook
 * knows which tin was opened.
 *
 * A line the catalogue cannot answer says so and offers a search, rather than
 * filling itself with the closest spelling. That is not a smaller feature than
 * matching: half of what a home recipe calls for is a pantry staple, and a
 * catalogue built from barcodes and restaurant menus is thinnest exactly there.
 *
 * The finished weight is the one number no page ever publishes, and it divides
 * every portion ever logged from the recipe — so it is asked for plainly, with
 * one tap of context ("was it baked, or was it a salad") standing in until the
 * dish has actually been on a scale.
 */
export function RecipeImportSheet({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: () => void
}) {
  const [url, setUrl] = useState('')
  const [phase, setPhase] = useState<Phase>({ name: 'input' })
  const loadingLine = useSassOnce(
    phase.name === 'loading' ? 'loading' : null,
    () => sassLine('loading', { surface: 'import' }) ?? 'Skaitomas puslapis…',
  )
  const [cookedWeightG, setCookedWeightG] = useState<number | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [searchingFor, setSearchingFor] = useState<Line | null>(null)
  const [method, setMethod] = useState<RecipeCooking>('unweighed')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setUrl('')
      setPhase({ name: 'input' })
      setCookedWeightG(null)
      setEditing(null)
      setSearchingFor(null)
      setMethod('unweighed')
    }
  }, [open])

  const lines = phase.name === 'review' ? phase.lines : []
  const resolved = lines.filter((l) => l.included && l.food && l.grams > 0)
  const rawTotal = resolved.reduce((sum, l) => sum + l.grams, 0)
  const missing = lines.filter((l) => l.included && !l.food)
  const missingG = missing.reduce((sum, l) => sum + l.grams, 0)
  /**
   * What went into the pot, whether or not the catalogue could answer for it.
   *
   * An unmatched ingredient still has weight. Leaving it out of the finished
   * weight divides the macros we *do* know by too few grams and makes every
   * portion read heavier than it was — and the weight used to be frozen at
   * whatever had matched the moment the page was read, so matching a line
   * afterwards (which the warning card asks the user to do) never corrected it.
   */
  const potTotalG = rawTotal + missingG
  const cookedG =
    cookedWeightG != null && cookedWeightG > 0
      ? cookedWeightG
      : (estimatedCookedWeight(potTotalG, method) ?? Math.round(potTotalG))

  const load = async () => {
    const trimmed = url.trim()
    if (trimmed === '') return
    setPhase({ name: 'loading' })
    try {
      const res = await api.post<WireRecipe>('/api/ai/recipe-url', { url: trimmed })
      const next: Line[] = res.ingredients.map((item, index) => {
        const best = item.candidates[0]
        return {
          key: `${index}-${item.name}`,
          raw: item.raw,
          heard: item.name,
          grams: item.grams ?? 0,
          // Only fill the line in when the catalogue genuinely answered it. A
          // near-spelling silently accepted is worse than a blank: the recipe
          // saves, the number is wrong, and nothing on screen ever said so.
          food: best && (best.strength ?? 1) >= AUTO_MATCH_FLOOR ? best.food : null,
          candidates: item.candidates.map((c) => c.food),
          included: !item.optional && item.grams != null && item.grams > 0,
        }
      })
      if (next.length === 0) {
        setPhase({
          name: 'failed',
          message: sassLine('error_import', {}) ?? 'Šiame puslapyje ingredientų sąrašo nerasta.',
        })
        return
      }
      // Deliberately not pre-filled: the finished weight is derived live from
      // the lines below, so matching one more of them moves it.
      setPhase({
        name: 'review',
        title: res.title ?? 'Receptas iš nuorodos',
        servings: res.servings,
        lines: next,
        source: res.source,
        truncated: res.truncated ?? false,
      })
    } catch (error) {
      const message =
        (error as { message?: string }).message ??
        sassLine('error_import', {}) ??
        'Nepavyko perskaityti šio puslapio.'
      setPhase({ name: 'failed', message })
    }
  }

  const patch = (key: string, change: Partial<Line>) =>
    setPhase((p) =>
      p.name !== 'review'
        ? p
        : { ...p, lines: p.lines.map((l) => (l.key === key ? { ...l, ...change } : l)) },
    )

  const commit = async () => {
    if (phase.name !== 'review' || saving) return
    if (resolved.length === 0 || cookedG <= 0) return
    setSaving(true)
    try {
      // The catalogue rows arrived from the server and have never been written
      // to this phone; a recipe pointing at foods it does not hold cannot
      // recompute its own macros later.
      for (const line of resolved) if (line.food) await cacheFood(line.food)
      await saveRecipe({
        name: phase.title,
        notes: null,
        cookedWeightG: cookedG,
        ingredients: resolved.map((l) => ({
          foodId: (l.food as LocalFood).id,
          rawWeightG: l.grams,
        })),
      })
      void syncNow()
      onOpenChange(false)
      onSaved?.()
    } catch (error) {
      setPhase({
        name: 'failed',
        message:
          (error as { message?: string }).message ??
          sassLine('error_save', {}) ??
          'Nepavyko išsaugoti.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Receptas iš nuorodos"
      footer={
        phase.name === 'review' ? (
          <div className="flex gap-2">
            <Button block onClick={() => setPhase({ name: 'input' })} disabled={saving}>
              Kita nuoroda
            </Button>
            <Button
              variant="primary"
              block
              onClick={() => void commit()}
              disabled={saving || cookedG <= 0 || rawTotal <= 0}
            >
              Išsaugoti
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 px-4 py-4">
        {phase.name === 'loading' && (
          <div className="flex flex-col items-center gap-3 py-10">
            <PersonaSpinner surface="import" />
            <p className="text-sm text-mute">{loadingLine}</p>
          </div>
        )}

        {(phase.name === 'input' || phase.name === 'failed') && (
          <>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              placeholder="https://…"
              className="h-11 rounded-[var(--radius-control)] border border-line bg-bg px-3 text-[1rem] text-ink placeholder:text-mute"
            />
            <Button
              variant="primary"
              block
              disabled={url.trim() === ''}
              onClick={() => void load()}
            >
              Perskaityti
            </Button>
            {phase.name === 'failed' && <p className="text-sm text-danger">{phase.message}</p>}
            {phase.name === 'input' && (
              <EmptyState
                title="Įklijuokite recepto nuorodą"
                hint="Ingredientai bus sugretinti su katalogu — patvirtinsite, kas iš tikrųjų buvo dedama."
              />
            )}
          </>
        )}

        {phase.name === 'review' && (
          <>
            <div className="flex flex-col gap-1">
              <span className="text-base text-ink">{phase.title}</span>
              <span className="text-xs text-mute">
                {phase.servings != null ? `${phase.servings} porcijos · ` : ''}
                {phase.source === 'json-ld'
                  ? 'ingredientai paimti iš puslapio duomenų'
                  : 'ingredientai atpažinti iš teksto'}
              </span>
            </div>

            {phase.truncated && (
              <Card className="border-warn/40 px-3 py-2.5">
                <p className="text-sm text-ink">Puslapyje ingredientų buvo daugiau, nei telpa</p>
                <p className="mt-0.5 text-xs text-mute">
                  Rodomi tik pirmieji — likusius pridėkite patys, kitaip receptas rodys mažiau
                  kalorijų, nei jame yra.
                </p>
              </Card>
            )}

            {/* Silence here would be the real bug: the recipe saves either way,
                and a dish missing a quarter of its butter reads as light. */}
            {missing.length > 0 && (
              <Card className="border-warn/40 px-3 py-2.5">
                <p className="text-sm text-ink">
                  {missing.length === 1
                    ? 'Vieno ingrediento kataloge nėra'
                    : `${missing.length} ingredientų kataloge nėra`}{' '}
                  <span className="tnum text-mute">({Math.round(missingG)} g)</span>
                </p>
                <p className="mt-0.5 text-xs text-mute">
                  Paspauskite eilutę ir paieškokite patys arba sukurkite produktą — kitaip receptas
                  rodys mažiau kalorijų, nei jame yra.
                </p>
              </Card>
            )}

            <CookedWeightField
              rawTotalG={potTotalG}
              method={method}
              onMethodChange={(next, suggested) => {
                setMethod(next)
                setCookedWeightG(suggested)
              }}
              value={cookedWeightG}
              onChange={setCookedWeightG}
            />

            <List className="overflow-hidden rounded-[var(--radius-card)] border border-line">
              {phase.lines.map((line, i) => {
                const expanded = editing === line.key
                const unmatched = line.included && !line.food
                return (
                  <div key={line.key}>
                    <ListRow
                      last={i === phase.lines.length - 1 && !expanded}
                      title={
                        line.food ? (
                          foodDisplayName(line.food)
                        ) : (
                          // The chip sits in the title rather than as an
                          // accessory: an accessory makes the row a div, and
                          // this row has to stay tappable to open the search.
                          <span className="flex items-center gap-1.5">
                            <span className="min-w-0 truncate">{line.heard}</span>
                            {unmatched && (
                              <Chip tone="warn" className="h-5 shrink-0 px-1.5">
                                nerasta
                              </Chip>
                            )}
                          </span>
                        )
                      }
                      subtitle={line.raw}
                      value={line.included ? Math.round(line.grams) : '—'}
                      meta={line.included ? 'g' : undefined}
                      className={line.included ? undefined : 'opacity-50'}
                      onActivate={() => setEditing(expanded ? null : line.key)}
                    />
                    {expanded && (
                      <div className="flex flex-col gap-3 border-b border-line bg-raised px-3 py-3">
                        <NumberField
                          label="Kiekis"
                          suffix="g"
                          value={line.grams}
                          onChange={(v) => patch(line.key, { grams: v ?? 0 })}
                        />
                        <div className="flex flex-col gap-1.5">
                          <span className="text-xs text-mute">
                            Recepte „{line.heard}“ — pasirinkite tinkamą
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {line.candidates.map((candidate) => (
                              <Pill
                                key={candidate.id}
                                active={candidate.id === line.food?.id}
                                onClick={() => patch(line.key, { food: candidate })}
                                className="whitespace-normal text-left"
                              >
                                {foodDisplayName(candidate)}
                              </Pill>
                            ))}
                            {line.candidates.length === 0 && (
                              <span className="text-sm text-mute">Kataloge atitikmens nerasta</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => setSearchingFor(line)}>
                            Ieškoti kataloge
                          </Button>
                          <button
                            type="button"
                            onClick={() => patch(line.key, { included: !line.included })}
                            className="text-sm text-accent-text"
                          >
                            {line.included ? 'Neįtraukti šios eilutės' : 'Įtraukti šią eilutę'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </List>
          </>
        )}
      </div>

      <FoodPickerSheet
        open={searchingFor != null}
        onOpenChange={(next) => !next && setSearchingFor(null)}
        title={searchingFor ? `„${searchingFor.heard}“` : 'Rasti produktą'}
        initialQuery={searchingFor?.heard ?? ''}
        onPick={(food) => {
          if (searchingFor) {
            patch(searchingFor.key, {
              food,
              // Picked by hand, so it belongs at the front of the chips too —
              // otherwise the choice vanishes the moment the row is reopened.
              candidates: [food, ...searchingFor.candidates.filter((c) => c.id !== food.id)],
              included: true,
            })
          }
          setSearchingFor(null)
        }}
      />
    </Sheet>
  )
}
